/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState, useRef, Dispatch, SetStateAction } from "react";

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  TableInstance,
} from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 PRO React TS components
import MDBox from "dashboard-template/components/MDBox";
import MDTypography from "dashboard-template/components/MDTypography";
import MDInput from "dashboard-template/components/MDInput";
import MDPagination from "dashboard-template/components/MDPagination";

// Material Dashboard 2 PRO React TS examples components
import DataTableHeadCell from "dashboard-template/examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "dashboard-template/examples/Tables/DataTable/DataTableBodyCell";
import { Pagination } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context/ThemeContext";

// Declaring props types for DataTable
export interface Props {
  entriesPerPage?:
    | false
    | {
        defaultValue: number;
        entries?: number[];
      };
  canSearch?: boolean;
  canSearchPlaceholder?: string;
  showTotalEntries?: boolean;
  table?: {
    columns: { [key: string]: any }[];
    rows: { [key: string]: any }[];
  };
  pagination?: {
    variant: "contained" | "gradient";
    color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark" | "light";
  };
  isSorted?: boolean;
  noEndBorder?: boolean;
  setModelFilter?: Dispatch<SetStateAction<string>>;
  setPageState?: Dispatch<SetStateAction<number>>;
  setPageSizeState?: Dispatch<SetStateAction<number>>;
  apiPagination?: boolean;
  apiPageTotal?: number;
  apiPageCurrentIndex?: number;
  apiRowsTotal?: number;
  maxColumnWidth?: number;
  sortIndicatorType?: "arrows" | "az" | "symbols" | "chevrons";
  isLoading?: boolean;
  disableFloatingScroll?: boolean;
  onSortChange?: (sortBy: string | null, sortOrder: "asc" | "desc" | null) => void;
  currentSortBy?: string;
  currentSortOrder?: "asc" | "desc" | null;
}

function DataTable({
  entriesPerPage = { defaultValue: 10, entries: [5, 10, 25, 50, 100] },
  canSearch = false,
  canSearchPlaceholder = "Search...",
  showTotalEntries = true,
  table,
  pagination = { variant: "gradient", color: "info" },
  isSorted = true,
  noEndBorder = false,
  setModelFilter,
  setPageState,
  setPageSizeState,
  apiPagination,
  apiPageCurrentIndex,
  apiPageTotal,
  apiRowsTotal,
  maxColumnWidth = 300,
  sortIndicatorType = "arrows",
  isLoading = false,
  disableFloatingScroll = false,
  onSortChange,
  currentSortBy = null,
  currentSortOrder = null,
}: Readonly<Props>): JSX.Element {
  let defaultValue: number;
  let entries: number[];

  if (entriesPerPage === false) {
    defaultValue = 100; 
    entries = [];
  } else {
    defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
    entries = entriesPerPage.entries ? entriesPerPage.entries : [5, 10, 25, 50, 100];
  }

  const columns = useMemo<any>(() => table.columns, [table]);
  const data = useMemo<any>(() => table.rows, [table]);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();
  
  // Refs for floating scrollbar
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const floatingScrollRef = useRef<HTMLDivElement>(null);
  const [showFloatingScroll, setShowFloatingScroll] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const shouldShowLoading = isLoading && !isSearching;

  // Enable manual sorting when using API pagination and onSortChange is provided
  const useManualSorting = apiPagination && !!onSortChange;

  const tableInstance = useTable(
    { 
      columns, 
      data, 
      initialState: { pageIndex: 0 },
      manualPagination: apiPagination,
      manualSortBy: useManualSorting,
      pageCount: apiPagination ? apiPageTotal : -1,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter, sortBy },
  }: TableInstance<object> = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Notify parent component when sort changes (for server-side sorting)
  useEffect(() => {
    if (useManualSorting && onSortChange) {
      if (sortBy && sortBy.length > 0) {
        const { id, desc } = sortBy[0];
        onSortChange(id, desc ? "desc" : "asc");
      } else {
        onSortChange(null, null);
      }
    }
  }, [sortBy, useManualSorting, onSortChange]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value: number) => {
    setPageSize(value);
    if (setPageState) {
      setPageState(1);
    }
    setPageSizeState && setPageSizeState(value);
  };

  // Check if horizontal scroll is needed and if original scrollbar is visible
  useEffect(() => {
    if (disableFloatingScroll) {
      setShowFloatingScroll(false);
      return;
    }

    const checkScrollNeeded = () => {
      if (tableContainerRef.current) {
        const { scrollWidth, clientWidth } = tableContainerRef.current;
        const needsHorizontalScroll = scrollWidth > clientWidth;

        if (needsHorizontalScroll) {
          // Check if the original scrollbar is visible in viewport
          const rect = tableContainerRef.current.getBoundingClientRect();
          const isOriginalScrollbarVisible = rect.bottom <= window.innerHeight;
          setShowFloatingScroll(!isOriginalScrollbarVisible);
        } else {
          setShowFloatingScroll(false);
        }
      }
    };

    checkScrollNeeded();
    window.addEventListener("resize", checkScrollNeeded);
    window.addEventListener("scroll", checkScrollNeeded);

    return () => {
      window.removeEventListener("resize", checkScrollNeeded);
      window.removeEventListener("scroll", checkScrollNeeded);
    };
  }, [data, columns, disableFloatingScroll]);

  // Sync scroll between table and floating scrollbar
  const handleTableScroll = () => {
    if (tableContainerRef.current && floatingScrollRef.current) {
      floatingScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  };

  const handleFloatingScroll = () => {
    if (tableContainerRef.current && floatingScrollRef.current) {
      tableContainerRef.current.scrollLeft = floatingScrollRef.current.scrollLeft;
    }
  };

  const gotoPageHandler = (page: number) => {
    gotoPage(page);
  };

  const apiEntriesStart = useMemo<number>(() => {
    const result = defaultValue * apiPageCurrentIndex - defaultValue + 1;
    if (result < 0) {
      return 0;
    }
    return result;
  }, [defaultValue, apiPageCurrentIndex, table.rows]);

  const apiEntriesEnd = useMemo<number>(() => {
    let result = 0;
    if (defaultValue === table.rows.length) {
      result = defaultValue * apiPageCurrentIndex;
    } else {
      result = defaultValue * (apiPageCurrentIndex - 1) + table.rows.length;
    }
    return result;
  }, [defaultValue, apiPageCurrentIndex, table.rows]);

  // A function that sets the sorted value for the table
  const setSortedValue = (column: any) => {
    let sortedValue;

    // For server-side sorting, use the currentSortBy/currentSortOrder props
    if (useManualSorting && currentSortBy !== undefined) {
      if (currentSortBy === column.id || currentSortBy === column.accessor) {
        sortedValue = currentSortOrder === "desc" ? "desc" : "asce";
      } else {
        sortedValue = "none";
      }
    } else if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Render the paginations
  const renderPagination = pageOptions.map((option: number) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPageHandler(option)}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }: any) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option: any) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }: any) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value: any) => {
    setIsSearching(true); 
    
    gotoPage(0);
    if (setPageState) {
      setPageState(1);
    }
    
    if (setModelFilter) {
      setModelFilter(value || "");
    } else {
      setGlobalFilter(value || undefined);
    }
    setTimeout(() => {
      setIsSearching(false);
    }, 3000);
  }, 500);

  // Setting the entries starting point
  const entriesStart = useMemo<number>(
    () => (pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1),
    [pageIndex, pageSize]
  );

  // Setting the entries ending point
  const entriesEnd = useMemo<number>(() => {
    let entriesEnd = 0;
    if (pageIndex === 0) {
      entriesEnd = pageSize;
    } else if (pageIndex === pageOptions.length - 1) {
      entriesEnd = rows.length;
    } else {
      entriesEnd = pageSize * (pageIndex + 1);
    }
    return entriesEnd;
  }, [pageIndex, pageSize, pageOptions.length]);

  function omitKey<T extends { key?: any }>(props: T): Omit<T, "key"> {
    const { key, ...rest } = props;
    return rest;
  }

  // Helper function to truncate text and determine if tooltip is needed
  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text || typeof text !== "string") return { truncated: text, needsTooltip: false };
    const needsTooltip = text.length > maxLength;
    const truncated = needsTooltip ? `${text.substring(0, maxLength)}...` : text;
    return { truncated, needsTooltip };
  };

  // Use page from react-table for consistent behavior
  const displayData = useMemo(() => {
    return page;
  }, [page]);

  return (
    <MDBox position="relative">
      {/* Fullscreen Loading Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        open={shouldShowLoading}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress 
            color="info" 
            size={60}
            thickness={4}
          />
          <MDTypography 
            variant="h6" 
            color="white" 
            sx={{ mt: 2 }}
          >
            {t("dataTableIndex.loading")}
          </MDTypography>
        </Box>
      </Backdrop>

      {shouldShowLoading ? (
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
          width="100%"
          sx={{ 
            minHeight: "400px",
            backgroundColor: darkMode ? "rgba(45, 55, 72, 0.8)" : "rgba(255, 255, 255, 0.8)"
          }}
        >
        </MDBox>
      ) : (
        <>
          <TableContainer
            ref={tableContainerRef}
            onScroll={handleTableScroll}
            sx={{
              boxShadow: "none",
              overflowX: "auto",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: darkMode ? "#2d3748" : "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: darkMode ? "#4a5568" : "#c1c1c1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: darkMode ? "#718096" : "#a8a8a8",
              },
            }}
          >
            {entriesPerPage || canSearch ? (
              <MDBox
                display="flex"
                flexDirection={{ xs: "column-reverse", sm: "row" }}
                justifyContent={entriesPerPage && canSearch ? "space-between" : canSearch ? "flex-end" : "flex-start"}
                alignItems={{ xs: "stretch", sm: "center" }}
                p={3}
              >
                {entriesPerPage && (
                  <MDBox display="flex" alignItems="center" mb={{ xs: 1, sm: 0 }}>
                    <Autocomplete
                      disableClearable
                      value={pageSize.toString()}
                      options={entries.map((a) => a.toString())}
                      onChange={(_event, newValue) => {
                        setEntriesPerPage(parseInt(newValue, 10));
                      }}
                      size="small"
                      sx={{ width: "5rem" }}
                      renderInput={(params) => <MDInput {...params} />}
                    />
                    <MDTypography variant="caption" color="secondary">
                      &nbsp;&nbsp; {t("dataTableIndex.entriesPerPage")}
                    </MDTypography>
                  </MDBox>
                )}
                {canSearch && (
                  <MDBox width="12rem" mb={{ xs: 1, sm: 0 }}>
                    <MDInput
                      id="cy-search-input"
                      placeholder={canSearchPlaceholder}
                      value={search}
                      size="small"
                      fullWidth
                      onChange={({ currentTarget }: any) => {
                        setSearch(currentTarget.value);
                        onSearchChange(currentTarget.value);
                      }}
                    />
                  </MDBox>
                )}
              </MDBox>
            ) : null}
            <Table {...getTableProps()}>
              <MDBox component="thead">
                {headerGroups.map((headerGroup: any, key: number) => (
                  <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column: any, key: number) => {
                      const isActionsColumn = column.id === "actions" || column.id === "viewAppointment" ||column.accessor === "actions" || (typeof column.Header === "string" && (column.Header.toLowerCase().includes("action") || column.Header.toLowerCase().includes("accion")));
                      const isSortable = !column.disableSortBy && !isActionsColumn;
                      const sortProps = !isActionsColumn && isSorted ? column.getSortByToggleProps() : {};
                      if (sortProps.title) {
                        sortProps.title = t("dataTableIndex.toggleSortBy");
                      }
                      return (
                        <DataTableHeadCell
                          key={key}
                          {...(isSortable ? column.getHeaderProps(isSorted && column.getSortByToggleProps() && sortProps) : {})}
                          width={column.width ? column.width : "auto"}
                          align={column.align ? column.align : "left"}
                          sorted={isSortable ? setSortedValue(column) : false}
                          sortIndicatorType={isSortable ? (column.sortIndicatorType || (column.isNumeric ? "numeric" : "az")) : undefined}
                        > 
                          <MDBox
                            sx={{
                              maxWidth: maxColumnWidth,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: "inherit",
                            }}
                          >
                            {column.render("Header")}
                          </MDBox>
                        </DataTableHeadCell>
                      );
                    })}
                  </TableRow>
                ))}
              </MDBox>
              <TableBody {...getTableBodyProps()}>
                {displayData.map((row: any, key: number) => {
                  prepareRow(row);
                  return (
                    <TableRow key={key} {...row.getRowProps()}>
                      {row.cells.map((cell: any, key: number) => {
                        const cellValue = cell.value;
                        const cellText =
                          typeof cellValue === "string" ? cellValue : String(cellValue || "");
                        const { truncated, needsTooltip } = truncateText(cellText);

                        return (
                          <DataTableBodyCell
                            key={key}
                            noBorder={noEndBorder && rows.length - 1 === key}
                            align={cell.column.align ? cell.column.align : "left"}
                            {...cell.getCellProps()}
                          >
                            <MDBox
                              sx={{
                                maxWidth: maxColumnWidth,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "inherit",
                              }}
                            >
                              {needsTooltip ? (
                                <Tooltip title={cellText} placement="top-start" arrow>
                                  <span style={{ cursor: "help" }}>{cell.render("Cell")}</span>
                                </Tooltip>
                              ) : (
                                cell.render("Cell")
                              )}
                            </MDBox>
                          </DataTableBodyCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            
            {!apiPagination && (
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
              >
                {showTotalEntries && (
                  <MDBox mb={{ xs: 3, sm: 0 }}>
                    <MDTypography variant="button" color="secondary" fontWeight="regular">
                      {t("dataTableIndex.showing")} {entriesStart} - {entriesEnd} {t("dataTableIndex.of")}{" "}
                      {rows.length} {t("dataTableIndex.entries")}
                    </MDTypography>
                  </MDBox>
                )}

                {pageOptions.length > 1 && (
                  <MDPagination
                    variant={pagination.variant ? pagination.variant : "gradient"}
                    color={pagination.color ? pagination.color : "info"}
                  >
                    {canPreviousPage && (
                      <MDPagination item onClick={() => previousPage()}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                    )}
                    {renderPagination.length > 6 ? (
                      <MDBox width="5rem" mx={1}>
                        <MDInput
                          inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                          value={customizedPageOptions[pageIndex]}
                          onChange={(event: any) => {
                            handleInputPagination(event);
                            handleInputPaginationValue(event);
                          }}
                        />
                      </MDBox>
                    ) : (
                      renderPagination
                    )}
                    {canNextPage && (
                      <MDPagination item onClick={() => nextPage()}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    )}
                  </MDPagination>
                )}
              </MDBox>
            )}

            {apiPagination && (
              <MDBox mb={{ xs: 3, sm: 0 }} pl={2}>
                <MDTypography variant="button" color="secondary" fontWeight="regular">
                  {t("dataTableIndex.showing")} {apiEntriesStart} - {apiEntriesEnd}{" "}
                  {t("dataTableIndex.of")} {apiRowsTotal} {t("dataTableIndex.entries")}
                </MDTypography>
              </MDBox>
            )}

            {apiPagination && (
              <MDBox p={1}>
                <Pagination
                  count={apiPageTotal}
                  page={apiPageCurrentIndex}
                  color="standard"
                  onChange={(event: React.ChangeEvent<unknown>, page: number) => {
                    setPageState && setPageState(page);
                  }}
                  sx={
                    darkMode
                      ? {
                          "& .MuiPaginationItem-root": {
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: "dark.main",
                            },
                          },
                          "& .Mui-selected": {
                            backgroundColor: "#0F141F",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "light.focus",
                            },
                          },
                        }
                      : {}
                  }
                />
              </MDBox>
            )}
          </TableContainer>
          {showFloatingScroll && (
            <MDBox
              position="sticky"
              bottom={0}
              left={0}
              right={0}
              zIndex={1000}
              sx={{
                backgroundColor: darkMode ? "rgba(45, 55, 72, 0.95)" : "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(4px)",
                borderTop: `1px solid ${darkMode ? "#4a5568" : "#e2e8f0"}`,
                padding: "4px 0",
              }}
            >
              <MDBox
                ref={floatingScrollRef}
                onScroll={handleFloatingScroll}
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "20px",
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": {
                    height: "12px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: darkMode ? "#2d3748" : "#f1f1f1",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: darkMode ? "#4a5568" : "#c1c1c1",
                    borderRadius: "6px",
                    border: `2px solid ${darkMode ? "#2d3748" : "#f1f1f1"}`,
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: darkMode ? "#718096" : "#a8a8a8",
                  },
                }}
              >
                <MDBox
                  sx={{
                    width: tableContainerRef.current?.scrollWidth || "100%",
                    height: "1px",
                  }}
                />
              </MDBox>
            </MDBox>
          )}
        </>
      )}
    </MDBox>
  );
}
export default DataTable;